import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  CardElementProps,
  CardExpiryElementProps,
  CardCvcElementProps,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import React from "react";
import { PaymentMethod } from "@stripe/stripe-js";
import {
  Alert,
  Box,
  BoxProps,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReivThemeDefinition } from "../theme/reivTheme";

const options = {
  style: {
    base: {
      fontFamily: ReivThemeDefinition.fonts.body,
      color: "rgb(26,32,44)",
      lineHeight: "24px",
      fontSize: "16px",
      letterSpacing: "0.025em",
      "::placeholder": {
        color: "#737373",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

/** A function that fetches the stripe payment method from the form elements */
export type GetPaymentMethodFn = () => Promise<PaymentMethod>;

export interface SubmitProps<Form> {
  getPaymentMethod: GetPaymentMethodFn;
  /** Additional form values */
  form?: Form;
}

interface StripeFormProps<Form> {
  /** Returns a function to fetch the stripe payment method */
  handleSubmit: (props: SubmitProps<Form>) => void;
  submitButtonText: string;
  isProcessing: boolean;
  errorMessage?: string;
}

const StripeForm: React.FC<StripeFormProps<any>> = (props) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isStripeReady, setIsStripeReady] = React.useState(false);

  React.useEffect(() => {
    if (stripe && elements) {
      setIsStripeReady(true);
    }
  }, [stripe, elements]);

  const { submitButtonText, isProcessing, errorMessage } = props;

  const getPaymentMethod = async () => {
    if (!stripe || !elements) {
      throw new Error(`Stripe isn't ready yet`);
    }
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement)!,
    });

    if (error) {
      throw error;
    }

    if (!paymentMethod) {
      throw new Error(
        `An error occurred while trying to process your payment.`
      );
    }

    return paymentMethod;
  };

  const handleSubmit = (event: React.FormEvent<HTMLDivElement>) => {
    event.preventDefault();

    // populate `form` with any other field values
    const form: any = {};
    Array.prototype.forEach.call(
      event.currentTarget.querySelectorAll("input"),
      function (input) {
        if (input.name) {
          form[input.name] = input.value;
        }
      }
    );

    props.handleSubmit({ getPaymentMethod, form });
  };

  return (
    <VStack as="form" spacing={4} onSubmit={handleSubmit} direction="column">
      {props.children}
      <CardNumber label="Card number" />
      <HStack w="100%" align="stretch">
        <CardExpiry options={options} mr="1" label="Expiry" />
        <CardCVC options={options} ml="1" label="CVC" />
      </HStack>
      {!!errorMessage && (
        <Alert status="error">
          <Text>{errorMessage}</Text>
        </Alert>
      )}

      <Button
        isFullWidth={true}
        isLoading={isProcessing}
        isDisabled={isStripeReady !== true}
        colorScheme="brand"
        type="submit"
      >
        {submitButtonText}
      </Button>
    </VStack>
  );
};

export default StripeForm;

interface StripeFieldWrapperProps extends BoxProps {
  label: string;
  name: string;
}
const StripeFieldWrapper = (props: StripeFieldWrapperProps) => {
  const { label, as, name, ...boxProps } = props;
  return (
    <FormControl id={name}>
      <FormLabel htmlFor={name}>
        {label}
        <Box
          padding="7px 16px"
          border="1px solid"
          borderRadius="md"
          borderColor="gray.200"
          as={as}
          options={options}
          {...boxProps}
        />
      </FormLabel>
    </FormControl>
  );
};

type Props = CardElementProps & BoxProps & { label: string };
const CardNumber = ({ label, ...props }: Props) => {
  return (
    <StripeFieldWrapper
      name="cardnumber"
      as={CardNumberElement}
      label={label}
      {...props}
    />
  );
};

type CardExpiryProps = CardExpiryElementProps & BoxProps & { label: string };
const CardExpiry = ({ label, ...props }: CardExpiryProps) => {
  return (
    <StripeFieldWrapper
      name="exp-date"
      label={label}
      as={CardExpiryElement}
      {...props}
    />
  );
};

type CardCVCProps = CardCvcElementProps & BoxProps & { label: string };
const CardCVC = ({ label, ...props }: CardCVCProps) => {
  return (
    <StripeFieldWrapper
      label="CVC"
      name="cvc"
      flex={1}
      as={CardCvcElement}
      {...props}
    />
  );
};
